import { CompositionStage, useComposition, animate, Easing, clamp, lerp } from "@/components/hero/stage";

/* Reproductive hero — "one arc, eight stages".
   Arc: a life draws itself as a single rising curve, the eight stages landing on it in order.
   Focus: the arc dims and one stage lifts off it, opening into its own world.
   Ask: the question that stage actually answers, and the sheet you take with you. */

const W = 800, H = 640;
const ROSE = "#DE6E96";
const PLUM = "#8E4B7C";
const INK = "#3A1A2A";
const FONT = "Outfit, Poppins, system-ui, sans-serif";
const SCENES = [
  { name: "Arc", dur: 6.5 },
  { name: "Focus", dur: 5 },
  { name: "Ask", dur: 5 },
];

/* x is age across a life, y is the height of the curve */
const STAGES = [
  { n: "Cycles", age: 14, tone: "#DE6E96" },
  { n: "Trying", age: 20, tone: "#E0808A" },
  { n: "Pregnancy", age: 30, tone: "#D97BA8" },
  { n: "First 40 days", age: 35, tone: "#E28FA0" },
  { n: "Contraception", age: 41, tone: "#B978B8" },
  { n: "Midlife", age: 48, tone: "#C86F9E" },
  { n: "Men", age: 55, tone: "#7E8BC9" },
  { n: "Loss", age: 25, tone: "#8E7BB5", below: true },
];

const AX = 60, AW = 660, AY = 300, AMP = 128;
const px = (age) => AX + ((age - 10) / 50) * AW;
const py = (age, below) => AY - Math.sin(((age - 10) / 50) * Math.PI) * AMP + (below ? 108 : 0);

const fade = (T, a, b, d = 0.6) => Math.min(clamp((T - a) / d, 0, 1), clamp((b - T) / d, 0, 1));
const FOCUS = 5; /* midlife */
/* the arc is a life read left to right, not a calendar */

function Atlas({ accent = ROSE }) {
  const { T: rawT, CUES: rawCues, authoredTotal, compact } = useComposition();
  const CUES = { Arc: 0, Focus: 6.5, Ask: 11.5, ...rawCues };
  const T = Number.isFinite(rawT) ? rawT : 0;
  const total = authoredTotal || 16.5;
  const A = (o) => animate(o)(T);

  const draw = A({ from: 0, to: 1, start: 0.6, end: 4.4, ease: Easing.easeInOutCubic });
  const focus = A({ from: 0, to: 1, start: CUES.Focus + 0.3, end: CUES.Focus + 2, ease: Easing.easeInOutCubic });
  const ask = fade(T, CUES.Ask + 0.2, total + 0.5, 0.7);
  const card = A({ from: 0, to: 1, start: CUES.Focus + 1.2, end: CUES.Focus + 2.6, ease: Easing.easeOutCubic });
  const chosen = STAGES[FOCUS];

  /* the curve, drawn to `draw` */
  const pts = [];
  for (let i = 0; i <= 60; i++) {
    const age = 10 + (i / 60) * 50;
    if (age > 10 + draw * 50) break;
    pts.push([px(age), py(age)]);
  }
  const d = pts.map((p, i) => (i ? "L " : "M ") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
  const head = pts[pts.length - 1] || [px(10), py(10)];

  return (
    <div style={{ position: "absolute", inset: 0, fontFamily: FONT }}>
      <div style={{
        position: "absolute", left: 40, top: 40, width: 620, height: 420, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(222,110,150," + (0.14 + focus * 0.08) + "), transparent 66%)",
      }} />

      {/* the arc */}
      <svg width={W} height={H} style={{ position: "absolute", left: 0, top: 0, opacity: 1 - focus * 0.72 }}>
        <line x1={AX} y1={AY + 132} x2={AX + AW} y2={AY + 132} stroke="rgba(58,26,42,0.12)" />
        {[20, 30, 40, 50].map((a) => (
          <g key={a}>
            <line x1={px(a)} y1={AY + 126} x2={px(a)} y2={AY + 138} stroke="rgba(58,26,42,0.2)" />
            <text x={px(a)} y={AY + 158} textAnchor="middle" fontSize="12" fill="rgba(58,26,42,0.38)" fontFamily={FONT}>{a}</text>
          </g>
        ))}
        <path d={d} fill="none" stroke={accent} strokeWidth="3.6" strokeLinecap="round" />
        <path d={d + " L " + head[0].toFixed(1) + " " + (AY + 132) + " L " + AX + " " + (AY + 132) + " Z"} fill="rgba(222,110,150,0.09)" />
        <circle cx={head[0]} cy={head[1]} r="12" fill={accent} opacity="0.2" />
        <circle cx={head[0]} cy={head[1]} r="5.5" fill={accent} />
      </svg>

      {/* the stage markers */}
      {STAGES.map((s, i) => {
        const at = 1.1 + i * 0.42;
        const p = A({ from: 0, to: 1, start: at, end: at + 0.7, ease: Easing.easeOutBack });
        const isFocus = i === FOCUS;
        const x = px(s.age), y = py(s.age, s.below);
        const fx = lerp(x, isFocus ? 132 : x, focus);
        const fy = lerp(y, isFocus ? 108 : y, focus);
        const op = clamp(p, 0, 1) * (isFocus ? 1 : 1 - focus * 0.94);
        return (
          <div key={s.n} style={{
            position: "absolute", left: fx - 8, top: fy - 8 + (isFocus || focus > 0.2 ? 0 : (i % 2 ? 30 : -30)), display: "flex", alignItems: "center", gap: 10,
            opacity: op, transform: "scale(" + (isFocus ? lerp(1, 1.35, focus) : 1) + ")", transformOrigin: "0 50%",
          }}>
            <span style={{
              width: 16, height: 16, borderRadius: "50%", background: s.tone,
              boxShadow: "0 0 0 " + (isFocus ? 6 * focus + 4 : 4) + "px " + s.tone + "26",
            }} />
            {s.below ? (
              <span style={{ position: "absolute", left: 0, top: -46, width: 2, height: 42, background: "rgba(142,123,181,0.4)" }} />
            ) : null}
            <span style={{
              font: "500 " + (isFocus ? 18 : 13.5) + "px/1 " + FONT, color: isFocus ? INK : "rgba(58,26,42,0.68)",
              whiteSpace: "nowrap", background: "rgba(255,255,255,0.92)", padding: "5px 11px", borderRadius: 999, boxShadow: "0 8px 18px -12px rgba(58,26,42,0.6)",
            }}>{s.n}</span>
          </div>
        );
      })}

      {/* caption over the arc */}
      <div style={{ position: "absolute", left: 58, top: 34, opacity: fade(T, -0.5, CUES.Focus + 0.6, 0.7) }}>
        <div style={{ font: "500 12.5px/1 " + FONT, letterSpacing: "0.2em", color: "rgba(58,26,42,0.42)" }}>ONE ARC · EIGHT STAGES · ONE OF THEM IS YOURS</div>
      </div>

      {/* the focused stage opens */}
      <div style={{ position: "absolute", left: 128, top: 168, width: 420, opacity: card, transform: "translateY(" + (1 - card) * 22 + "px)" }}>
        <div style={{ font: "500 12px/1 " + FONT, letterSpacing: "0.2em", color: chosen.tone }}>YOUR ATLAS · {chosen.n.toUpperCase()}</div>
        <div style={{ font: "400 34px/1.14 " + FONT, color: INK, marginTop: 14 }}>
          Perimenopause, without<br />being told it is nothing.
        </div>
        {!compact && (
          <div style={{ display: "flex", gap: 8, marginTop: 22, flexWrap: "wrap" }}>
            {["waking at four", "new anxiety", "words going missing", "joints"].map((t, i) => {
              const at = CUES.Focus + 2.2 + i * 0.22;
              const p = A({ from: 0, to: 1, start: at, end: at + 0.6, ease: Easing.easeOutCubic });
              return (
                <span key={t} style={{
                  font: "400 14px/1 " + FONT, color: INK, padding: "10px 15px", borderRadius: 999,
                  background: "rgba(255,255,255,0.85)", border: "1px solid rgba(58,26,42,0.08)",
                  opacity: p, transform: "translateY(" + (1 - p) * 10 + "px)",
                }}>{t}</span>
              );
            })}
          </div>
        )}
      </div>

      {/* the sheet you take with you */}
      <div style={{
        position: "absolute", left: 128, top: 372, width: 440, padding: "24px 26px", borderRadius: 24,
        background: "linear-gradient(150deg, #fff, #FFF6FA)", border: "1px solid rgba(58,26,42,0.08)",
        boxShadow: "0 40px 80px -56px rgba(58,26,42,0.9)",
        opacity: ask, transform: "translateY(" + (1 - ask) * 24 + "px)",
      }}>
        <div style={{ font: "500 11.5px/1 " + FONT, letterSpacing: "0.18em", color: PLUM }}>THE SHEET YOU PUT IN FRONT OF A DOCTOR</div>
        {["Periods closer together, and heavier", "Awake at four, every night", "Rage out of proportion", "A normal blood test, and no answers"].map((t, i) => {
          const at = CUES.Ask + 0.7 + i * 0.3;
          const p = A({ from: 0, to: 1, start: at, end: at + 0.5, ease: Easing.easeOutCubic });
          return (
            <div key={t} style={{ display: "flex", alignItems: "center", gap: 12, marginTop: i ? 12 : 16, opacity: p }}>
              <span style={{
                width: 19, height: 19, borderRadius: 6, background: ROSE, display: "flex", alignItems: "center", justifyContent: "center",
                font: "600 11px/1 " + FONT, color: "#fff",
              }}>✓</span>
              <span style={{ font: "400 15px/1.3 " + FONT, color: INK }}>{t}</span>
            </div>
          );
        })}
      </div>

      <div style={{ position: "absolute", right: 4, bottom: 6, display: "flex", gap: 7 }}>
        {SCENES.map((s, i) => {
          const on = (i === 0 && T < CUES.Focus) || (i === 1 && T >= CUES.Focus && T < CUES.Ask) || (i === 2 && T >= CUES.Ask);
          return <span key={s.name} style={{ width: on ? 22 : 8, height: 8, borderRadius: 4, background: on ? ROSE : "rgba(58,26,42,0.16)", transition: "width 0.4s" }} />;
        })}
      </div>
    </div>
  );
}

export default function ReproHero({ accent = ROSE }) {
  return (
    <CompositionStage width={W} height={H} scenes={SCENES} minScale={0.52} maxHeight={600} style={{ maxWidth: 780, marginLeft: "auto", marginRight: "auto" }}>
      <Atlas accent={accent} />
    </CompositionStage>
  );
}
